import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { v4 as uuid } from 'uuid';

import { AutoComplete } from 'primeng/autocomplete';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged, map, skipWhile, startWith, takeUntil, tap } from 'rxjs/operators';

import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { SBaseDisplay, SDate } from '@summit/core';
import { isGuid, SummitIdObject } from '@summit/models';
import { CrudCacheParameters } from '@summit/services';
import { BlurDirective, LayoutService } from '@summit/ui';
import { Dayjs } from 'dayjs';
import { AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { FloatLabel } from 'primeng/floatlabel';
import { InputGroup } from 'primeng/inputgroup';
import { Skeleton } from 'primeng/skeleton';
import { SummitControl } from '../../models';
import { SFormErrorInputComponent } from '../s-form-error-input';
import { AutocompleteLabelDirective } from './autocomplete-label-directive';
@Component({
  selector: 's-form-autocomplete-text',
  templateUrl: './s-form-autocomplete.component.html',
  styleUrls: ['./s-form-autocomplete.component.scss'],
  standalone: true,
  imports: [
    SFormErrorInputComponent,
    AutoCompleteModule,
    InputGroup,
    FloatLabel,
    FormsModule,
    BlurDirective,
    AsyncPipe,
    NgTemplateOutlet,
    Skeleton,
    AutocompleteLabelDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormAutoCompleteComponent<T extends SummitIdObject, U extends SummitIdObject>
  extends SummitControl<T>
  implements OnInit
{
  @Input() items$: Observable<U[]>;
  @Input() cacheParameters: Observable<CrudCacheParameters>;
  @Input() defaultText?: string;
  @Input() keyProperty: keyof U & string;
  @Input() displayProperty: keyof U & string;
  @Input() customFilter: (item: U) => boolean;
  @Input() autoSelectEntry = true;
  @Input() showNewButton = false;
  @Input() canCreate = false;
  @Input() showDriverToggle = false;
  @Input() multiple = false;
  @Input() perfectMatchTest: (search: string, item: U) => boolean; //__display is added to item
  @Output() addEvent = new EventEmitter<Event>();
  @Output() comboBlur = new EventEmitter<Event>();
  @Output() comboChange = new EventEmitter<string>();
  @ViewChild('input', { static: false }) input: AutoComplete;
  @ContentChild('prefix') prefix: TemplateRef<unknown>;
  @ContentChild('suffix') suffix: TemplateRef<unknown>;
  formData: string | string[] | any;
  lastSearchTerm: string;
  lastFormChange: string;

  autoHighlight = false;
  filteredOptionsBs = new BehaviorSubject<{ id: string; __display: string }[]>([]);
  filteredOptions$: Observable<{ id: string; __display: string }[]> = this.filteredOptionsBs.asObservable();
  //createObject = true;
  customFilterChanged$ = new BehaviorSubject<boolean>(false);
  loading$: Observable<boolean>;
  //defaultText$: Observable<string>;
  public itemsCache: Map<U[keyof U], U> = new Map();

  private keyboardSelectedOption?: string | number; // unsure of use in prime
  createObject: boolean;
  selectionCache?: U | U[];
  field?: string;
  inputId: string;
  hasFocus = false;

  constructor(protected layout: LayoutService) {
    super();
    this.field = this.getField.bind(this) as unknown as string;
    this.createObject = false;
  }

  override ngOnInit() {
    if (this.multiple) this.formData = [];
    super.ngOnInit();
    this.inputId = (this.fieldName ?? uuid()) + this.groupName?.toString();
    this.keyProperty ??= 'id' as keyof U & string;
    this.displayProperty ??= 'name' as keyof U & string;

    //console.log(`${this.fieldName} has items$`, this.items$);

    this.items$?.pipe(takeUntil(this.disposing$)).subscribe((items) => {
      //console.log('Using Items: ', items)
      this.itemsCache = new Map(items.map((item) => [item[this.keyProperty], item]));
      //console.log('itemsCache', this.itemsCache);
    });
    //const debugging = false && this.fieldName == 'destNum';
    if (this.schema) {
      this.attribute = this.schema.getAttribute(this.fieldName as keyof T & string, this.groupName);
      if (!this.attribute) {
        console.error(
          `Could not find Property: ${this.fieldName} in group ${this.groupName ?? 'root'} in Tree:`,
          this.schema
        );
      }
    }
    this.label = this.label ?? this.attribute?.displayName;
    this.defaultText = this.defaultText ?? this.label;
    this.displayHint = this.displayHint ?? this.attribute?.displayHint;
    if (this.form) {
      //* Access to control
      this.myControl = this.fieldName ? (this.form.get(this.fieldName) ?? undefined) : undefined;
      //console.log(`got control from form`, this.myControl, this.form);

      //* Keep Filter List Up to date and selection in sync
      //console.log('items$', this.items$);
      //console.log('customFilterChanged$', this.customFilterChanged$);
      if (this.items$) {
        combineLatest({
          items: this.items$,
          valueChanges: this.myControl?.valueChanges.pipe(startWith(this.myControl?.value))
        })
          .pipe(
            skipWhile((combined) => !combined.items || this.itemsCache.size === 0), //wait for items to load before processing value changes to prevent false no-match scenarios
            distinctUntilChanged(),
            takeUntil(this.disposing$)
          )
          .subscribe((combined) => {
            const { items, valueChanges } = combined;
            const value = valueChanges as string | number | (string | number)[] | undefined;
            if (this.multiple) {
              //primeng can sometimes pass a string instead of an array when multiple is true, so normalize to array
              const trueValue = Array.isArray(value) ? (value as (string | number)[]) : [value];
              this.selectionCache = items.filter((item) =>
                trueValue.includes(item[this.keyProperty] as unknown as string | number)
              );
              this.formData = this.selectionCache;
            } else {
              const selItem = items.find((item) => item[this.keyProperty] === value || item.id === value);
              if (selItem) {
                if (this.formData !== selItem) {
                  this.formData = selItem;
                }
              } else if (this.canCreate) {
                //create a dummy object with just the id to display in the input, this allows for values that aren't in the list but still need to be displayed (like current value when list is filtered)
                if (value || value === 0) {
                  const dummy = {
                    [this.keyProperty]: value,
                    __display: String(value)
                  } as unknown as U;
                  this.formData = dummy;
                }
              }
            }
          });
        combineLatest([this.items$, this.customFilterChanged$])
          .pipe(
            // distinctUntilChanged(),
            map(([itemList, _filter]) => {
              //this.itemsCache = itemList; //to keep in sync since the subscription above isn't timely
              //console.log(`${this.fieldName.toString()} got new items`, itemList);
              return Array.isArray(itemList)
                ? itemList.filter((item) => !this.customFilter || this.customFilter(item))
                : [];
            }),
            //tap((items) => console.log(`${this.fieldName}: items`, items)),
            tap((items) => {
              const currentValue = this.myControl?.value;
              if ((currentValue || currentValue === 0) && !isGuid(currentValue)) {
                //console.log("Lookup update: value isn't a guid", currentValue);
                const currentObject = items.find(
                  (item) => this.getDisplayProperty(item) === String(currentValue) || item.id == currentValue
                );
                if (currentObject) {
                  this.setValue(currentObject[this.keyProperty] as string | number, true);
                }
              }
            }),
            takeUntil(this.disposing$)
          )
          .subscribe((items) => {
            const localValues = items.map((item) => {
              return {
                id: String(item[this.keyProperty] ?? item.id ?? ''),
                __display: this.getDisplayProperty(item) ?? ''
              };
            });
            this.filteredOptionsBs.next(localValues);
            this.setValue(this.myControl?.value as string | number | undefined);
          });
      }
    } else {
      console.error(`No form found for ${this.fieldName}`);
    }
    if (this.readonly) {
      this.myControl?.disable();
    }

    //this.autoInput.focusInput();
  }

  onAdd() {
    console.warn('Add button clicked, but no handler implemented');
  }
  onFocus() {
    this.hasFocus = true;
  }

  queryFilter(event) {
    //console.log('query Filter', event, this.itemsCache);
    this.lastSearchTerm = event.query;
    const currentValue = this.myControl?.value;
    const selectedIds = this.multiple && Array.isArray(currentValue) ? currentValue.map((v) => String(v)) : [];
    
    if (event.query.length > 0) {
      const query = event?.query?.toLowerCase();
      const filteredList = this.itemsCache
        ? [...this.itemsCache.values()].filter((item) => {
            // Check if item matches search query
            const matchesQuery =
              ((item['__display'] ?? this.getDisplayProperty(item))?.toString().toLowerCase()?.indexOf(query) ?? -1) >=
              0;
            
            // In multiple mode, exclude already selected items
            if (this.multiple && selectedIds.length > 0) {
              const itemKey = String(item[this.keyProperty]);
              return matchesQuery && !selectedIds.includes(itemKey);
            }
            
            return matchesQuery;
          })
        : [];
      //console.log('filteredList', filteredList);
      this.filteredOptionsBs.next(
        filteredList.map((item) => {
          return {
            id: String(item[this.keyProperty] ?? item.id ?? ''),
            __display: String(item['__display'] ?? this.getDisplayProperty(item) ?? '')
          };
        })
      );
    } else {
      const local = this.itemsCache
        ? [...this.itemsCache.values()]
            .filter((item) => {
              // In multiple mode, exclude already selected items
              if (this.multiple && selectedIds.length > 0) {
                const itemKey = String(item[this.keyProperty]);
                return !selectedIds.includes(itemKey);
              }
              return true;
            })
            .map((item) => {
              return {
                id: String(item[this.keyProperty] ?? item.id ?? ''),
                __display: String(item['__display'] ?? this.getDisplayProperty(item) ?? '')
              };
            })
        : [];
      this.filteredOptionsBs.next(local);
    }
  }
  getField() {
    // console.log('getField', this.selectionCache);
    return Array.isArray(this.selectionCache)
      ? this.displayFn(this.selectionCache?.[0]?.id ?? undefined)
      : this.displayFn(this.selectionCache?.id ?? undefined);
  }
  displayFn(id?: string | number | { __display: string }): string {
    //console.log(`${this.fieldName.toString()} Create display name for ${id}`, id);

    // Handle case where PrimeNG passes the full object with __display
    if (id && typeof id === 'object' && '__display' in id) {
      return `${id.__display}`;
    }

    // Handle case where PrimeNG passes the full cached item object
    if (id && typeof id === 'object' && this.displayProperty in (id as any)) {
      return `${this.getDisplayProperty(id as unknown as U) ?? ''}`;
    }

    if (!id && id !== 0) {
      return '';
    }

    const displayItem = this.itemsCache
      ? Array.from(this.itemsCache)?.find(
          ([itemId, item]) => itemId?.toString() === id?.toString() || itemId == id || item.id == id
        )?.[1]
      : undefined;

    return `${this.getDisplayProperty(displayItem) ?? ''}`;
  }

  getDisplayProperty(selectedItem: U | undefined): string | null {
    // console.log('getDisplayProperty', selectedItem);
    if (selectedItem == null || selectedItem == undefined) return null;
    if (this.displayProperty === 'toShortString') {
      return (selectedItem as unknown as SBaseDisplay)?.toShortString();
    }
    return selectedItem[this.displayProperty]?.toString() ?? null;
  }

  formChanged($event) {
    //console.log('formChanged', this.formData, $event);
    this.lastFormChange = $event;

    // In multiple mode, formData is an array of objects, extract IDs for form control
    if (this.multiple && Array.isArray(this.formData)) {
      const ids = this.formData
        .map((item) => (typeof item === 'object' && item && 'id' in item ? item.id : item[this.keyProperty]))
        .filter((id) => id != null);

      // console.log('formChanged: extracted ids', ids);
      if (JSON.stringify(ids) !== JSON.stringify(this.myControl?.value)) {
        // console.log('formChanged: calling setValue with', ids);
        this.myControl?.setValue(ids);
      }
    }
    //this.myControl?.setValue($event);
  }
  async setValue(id: (string | number)[] | string | number | undefined, initialLoad = false) {
    if (this.multiple) {
      //primeng can sometimes pass a string instead of an array when multiple is true, so normalize to array
      var idArray = Array.isArray(id) ? id : [id];
      //if the arrays are the same, no need to update
      if (
        idArray.length === (this.myControl?.value?.length ?? 0) &&
        JSON.stringify(idArray) === JSON.stringify(this.myControl?.value)
      ) {
        // console.log(`${this.fieldName.toString()} setValue: value is the same as current, no update needed`, idArray);
        return;
      }
      this.myControl?.setValue(idArray);
    } else {
      if (this.myControl?.value === id) return; //no change
      this.myControl?.setValue(id);
      this.cd.markForCheck();
    }
    return;
  }

  async onShow() {
    const isMobile = await firstValueFrom(this.layout.mobile$);
    if (isMobile) {
      document.getElementById(this.inputId)?.blur();
    }
  }

  onClear() {
    if (this.multiple) {
      this.selectionCache = [];
      this.myControl?.setValue([]);
    } else {
      this.myControl?.setValue('');
    }
    this.myControl?.updateValueAndValidity();
  }

  onSelection($event: AutoCompleteSelectEvent) {
    // In multiple mode, formData is automatically updated by PrimeNG's ngModel
    // Extract IDs from the formData array and sync to form control
    if (this.multiple && Array.isArray(this.formData)) {
      const ids = this.formData
        .map((item) => {
          if (typeof item === 'object' && item && 'id' in item) {
            return item.id;
          }
          return item[this.keyProperty];
        })
        .filter((id) => id != null) as (string | number)[];
      this.setValue(ids);
      return;
    }

    this.setValue($event?.value?.[this.keyProperty] as string | number | undefined);
    return;
  }

  disableOnBlurTil: Dayjs;
  async onDropDownClick(_event) {
    if (this.input?.overlayVisible ?? false) {
      this.input.hide();
    } else {
      this.input.show();
      if (this.layout.isMobile()) {
        this.disableOnBlurTil = SDate.newUtc().add(100, 'milliseconds');
        document.getElementById(this.inputId)?.blur();
      }
    }
  }

  onBlur(_event?) {
    this.hasFocus = false;
    if (this.disableOnBlurTil && this.disableOnBlurTil.isAfter(SDate.newUtc())) {
      //console.warn('Ignoring onBlur due to recent dropdown click');
      return;
    }
    //use last search term if available, otherwise fall back to last form change (use case when quick entry and tabbing)
    const lookupValue = this.lastSearchTerm ?? this.lastFormChange;
    //set timeout to allow for quick entry & tabbing through fields without prematurely hiding the dropdown, but if there's a lookup value and the dropdown is open, hide it to prevent confusion
    setTimeout(() => {
      if (lookupValue && this.input?.overlayVisible) {
        this.input.hide();
      }
    }, 200);
    this.lastSearchTerm = undefined;
    const availOptionCount = this.filteredOptionsBs.value.length;
    //console.log('Available Option Count:', lookupValue, availOptionCount);
    if (availOptionCount == 1 && !isGuid(lookupValue)) {
      //console.log('On Blur: Highlight Option', availOptionCount);
      const singleMatch = this.filteredOptionsBs.value[0];
      //console.log('Only one left', singleMatch);
      if (singleMatch) {
        const matchedId = singleMatch.id as string | number;
        this.addSelection(matchedId);
        this.input.hide();
        this.cd.detectChanges();
        return;
      }
    }
    //console.log('On Blur: No perfect FILTER match found', lookupValue);
    if (!isGuid(lookupValue) && lookupValue?.length > 0) {
      //console.log('On Blur: No perfect match found', lookupValue);
      if (availOptionCount !== 1) {
        //console.log('On Blur: No perfect match found, doing custom search');
        if (this.perfectMatchTest) {
          const perfectMatch = this.itemsCache
            ? [...this.itemsCache.values()].find((item) => this.perfectMatchTest(lookupValue, item))
            : undefined;
          if (perfectMatch) {
            //console.log('Perfect match found', perfectMatch);
            const matchedId = perfectMatch[this.keyProperty] as string | number;
            this.addSelection(matchedId);
            this.input.hide();
            this.cd.detectChanges();
            return;
          }
          //console.log('No perfect match found');
        } else {
          //console.log('Doing My own search', lookupValue);
          if (!lookupValue) return;
          const inputValue = lookupValue.toString().toUpperCase();
          const possibleValues = this.itemsCache
            ? [...this.itemsCache.values()].filter((item) => {
                const testString = item['__display']?.toString();
                if (!testString || testString.length === 0) return false;
                return testString.toUpperCase().indexOf(inputValue.toUpperCase()) >= 0;
              })
            : [];
          //console.log('Possible Values', possibleValues);
          if (possibleValues.length == 1) {
            if (this.fieldName === 'employeeID')
              console.log('[setValue called from] onBlur (custom search single match)', {
                possibleValue: possibleValues[0]
              });
            const matchedId = possibleValues[0][this.keyProperty] as string | number;
            this.addSelection(matchedId);
            this.input.hide();
            this.cd.detectChanges();
            return;
          }
        }
      }
      //console.log('No perfect match found: Leaving value', lookupValue);
      if (this.canCreate) {
        this.createObject = true;
        this.setValue(lookupValue);
      }
    }
  }

  /**
   * Helper method to add a selection in multiple mode.
   * Appends the ID to existing selections if not already present.
   * In single mode, replaces the current value.
   */
  private addSelection(matchedId: string | number): void {
    const rawValue: unknown = this.myControl?.value;

    if (this.multiple) {
      let currentValues: Array<string | number>;

      if (Array.isArray(rawValue)) {
        currentValues = rawValue as Array<string | number>;
      } else if (
        rawValue !== null &&
        rawValue !== undefined &&
        (typeof rawValue === 'string' || typeof rawValue === 'number')
      ) {
        currentValues = [rawValue as string | number];
      } else {
        currentValues = [];
      }

      // Only add if not already in the list (compare using a consistent string representation)
      const hasMatchedId = currentValues.some(
        value => String(value) === String(matchedId)
      );

      if (!hasMatchedId) {
        this.setValue([...currentValues, matchedId]);
      }
    } else {
      this.setValue(matchedId);
    }
  }

  updateSelection(): void {
    const id = this.myControl?.value;
    if (id || id === 0) {
      this.setValue(id);
    }
  }

  onKeyUp(event: KeyboardEvent) {
    //console.log('onKeyUp', event);
    if (event.key === 'Enter' || event.key === ';') {
      this.onBlur();
    }
  }

  /*
  below is not currently used within this
*/
  public async triggerAdd(params: Event) {
    // console.log(`add new entry with `, params);
    this.addEvent.emit(params);
  }
  //Used in quick Create: Truck Alias
  getValue() {
    // console.log(`GetValue returning value of: ${this.myControl.value}`, this.myControl)
    return this.myControl?.value;
  }

  getDisplayValue(id: string) {
    // console.log(`GetDisplayValue returning value of: ${this.myControl.value}`, this.myControl)
    return this.displayFn(id);
  }

  getDisplayForOption(id: string | number): string {
    // This method is specifically for PrimeNG optionLabel
    // It should only get the display text for a given ID without complex object handling
    if (!id && id !== 0) return '';
    const item = this.itemsCache?.get(id as U[keyof U]);
    return this.getDisplayProperty(item) ?? '';
  }
}
